/**
 * Unified Component Size System
 *
 * This module provides standardized size constants and utilities for all components
 * in the shared-components library. It ensures consistency across Button, LoadingSpinner,
 * and other components while providing backward compatibility during migration.
 *
 * @see shared-components/CLAUDE.md for usage guidelines
 */

/**
 * Standard component sizes using consistent abbreviated format
 * These should be used as the primary size values for all components
 */
export const COMPONENT_SIZES = {
  /** Extra small - minimal space */
  XS: "xs",
  /** Small - compact size */
  SM: "sm",
  /** Medium - default size */
  MD: "md",
  /** Large - spacious size */
  LG: "lg",
  /** Extra large - maximum size */
  XL: "xl",
};

/**
 * Mapping table for backward compatibility and common aliases
 * Allows migration from various naming conventions to the unified system
 */
export const SIZE_ALIASES = {
  // Full word variants (most common in existing codebase)
  tiny: COMPONENT_SIZES.XS,
  small: COMPONENT_SIZES.SM,
  medium: COMPONENT_SIZES.MD,
  default: COMPONENT_SIZES.MD, // Legacy default
  large: COMPONENT_SIZES.LG,
  xlarge: COMPONENT_SIZES.XL,
  huge: COMPONENT_SIZES.XL,

  // Already correct abbreviated forms (pass through)
  xs: COMPONENT_SIZES.XS,
  sm: COMPONENT_SIZES.SM,
  md: COMPONENT_SIZES.MD,
  lg: COMPONENT_SIZES.LG,
  xl: COMPONENT_SIZES.XL,

  // Legacy LoadingSpinner variants are already handled by full words above
};

/**
 * Normalizes a size value to the standard abbreviated format
 *
 * @param {string} size - The size value to normalize (e.g., "small", "large", "sm")
 * @returns {string} - The normalized size value (e.g., "sm", "lg")
 *
 * @example
 * normalizeSize("small") // returns "sm"
 * normalizeSize("large") // returns "lg"
 * normalizeSize("sm")    // returns "sm" (already normalized)
 * normalizeSize("invalid") // returns "md" (fallback to default)
 */
export const normalizeSize = (size) => {
  if (!size || typeof size !== "string") {
    return COMPONENT_SIZES.MD; // Default fallback
  }

  const normalized = SIZE_ALIASES[size.toLowerCase()];
  return normalized || COMPONENT_SIZES.MD; // Fallback to medium if unknown
};

/**
 * Validates that a size value is part of the standardized system
 *
 * @param {string} size - The size value to validate
 * @returns {boolean} - True if the size is valid
 *
 * @example
 * isValidSize("sm") // returns true
 * isValidSize("small") // returns true (valid alias)
 * isValidSize("invalid") // returns false
 */
export const isValidSize = (size) => {
  if (!size || typeof size !== "string") {
    return false;
  }

  return (
    Object.values(COMPONENT_SIZES).includes(size) ||
    size.toLowerCase() in SIZE_ALIASES
  );
};

/**
 * Gets all valid size values (both standard and aliases)
 * Useful for PropTypes validation and documentation
 *
 * @returns {string[]} - Array of all valid size values
 */
export const getAllValidSizes = () => [
  ...Object.values(COMPONENT_SIZES),
  ...Object.keys(SIZE_ALIASES),
];

/**
 * Reverse mapping to get display names for sizes
 * Useful for debugging and development tools
 */
export const SIZE_DISPLAY_NAMES = {
  [COMPONENT_SIZES.XS]: "Extra Small",
  [COMPONENT_SIZES.SM]: "Small",
  [COMPONENT_SIZES.MD]: "Medium (Default)",
  [COMPONENT_SIZES.LG]: "Large",
  [COMPONENT_SIZES.XL]: "Extra Large",
};

/**
 * Migration helper to update a component's size prop mapping
 * during the transition period
 *
 * @param {Object} sizeMap - Component-specific size-to-class mapping
 * @param {string} size - The size prop value to look up
 * @returns {string} - The CSS class(es) for the normalized size
 *
 * @example
 * const sizeClasses = {
 *   xs: "h-4 w-4",
 *   sm: "h-6 w-6",
 *   md: "h-8 w-8",
 *   lg: "h-10 w-10",
 *   xl: "h-12 w-12"
 * };
 *
 * getSizeClass(sizeClasses, "small") // returns "h-6 w-6"
 * getSizeClass(sizeClasses, "large") // returns "h-10 w-10"
 */
export const getSizeClass = (sizeMap, size) => {
  const normalizedSize = normalizeSize(size);
  return sizeMap[normalizedSize] || sizeMap[COMPONENT_SIZES.MD] || "";
};
